
import { CSSProperties, FC, Fragment, memo, MouseEvent, ReactNode, useCallback } from 'react'
import { useState } from 'react'
import { CloseSvg, WindowDetachSvg, WindowResizeSvg, WindowRightSvg } from '../svgs/Svgs'
import {
    HeaderAction,
    HeaderActionLabel,
    HeaderActionsContainer,
    Modal,
    ModalBody,
    ModalHeader,
    ModalModeType,
    WindowSize
} from '../modals/Modal'
import './OrbitalInsights.scss'
import { convertDecimalMinutesToMMSS, mergeClassNames } from '../../../utils/commonFunctions'
import SunExposer from './SunExposer'
import { OrbitalInsightSatelliteType, OrbitStartOptionsType } from './orbital-insights-type'
import PassDetailsPanel from './PassDetailsPanel'
import { SCENARIO_OPERATION_TYPES } from '../../simulate/constants'

export type DurationType = 'SEC' | 'MIN'

export type WindowKeys = 'exposure' | 'contacts' | 'features'


interface OrbitalInsightsProps {
    tabs: {
        label: string
        value: string
        icon: ReactNode
    }[]
    style?: CSSProperties
    modalId?: string
    className?: string
    open: boolean
    pinned?: boolean
    windowSize: WindowSize
    disabled?: boolean
    selectedTab: WindowKeys
    detachedTabs: string[]
    showDetachedIcon: boolean
    data: OrbitalInsightSatelliteType | null;
    onFocus: (e: MouseEvent) => void
    onModeClick: (e: MouseEvent, mode: ModalModeType) => void
    onTabSelect: (e: MouseEvent, tab: WindowKeys) => void
    onDetached: (e: MouseEvent, tab: WindowKeys) => void
    orbitStartOption?: OrbitStartOptionsType
    scenarioOperationType?: 'LEOP' | 'CONOPS'
}

/**
 * A component that displays orbital insights for a satellite.
 * 
 * @param {CSSProperties} [placement] - Custom placement styles for the modal.
 * @param {string} [className] - Additional class names.
 * @param {boolean} open - Whether the modal is open.
 * @param {boolean} pinned - Whether the modal is pinned.
 * @param {boolean} disabled - Whether the modal is disabled.
 * @param {any} data - The satellite data.
 * @returns {JSX.Element} The orbital insights modal.
 */
const OrbitalInsights: FC<OrbitalInsightsProps> = memo(({
    open,
    tabs,
    data,
    windowSize,
    style = {},
    selectedTab,
    className = '',
    modalId = 'orbital__insights__modal',
    pinned = false,
    disabled = false,
    detachedTabs = [],
    showDetachedIcon = false,
    onFocus,
    onModeClick,
    onTabSelect,
    onDetached,
    orbitStartOption = 'Start Time',
    scenarioOperationType = SCENARIO_OPERATION_TYPES.CONOPS
}) => {
    const [durationType, setDurationType] = useState<DurationType>('SEC')

    const getDuration = useCallback((duration: number) => {
        const minSec = convertDecimalMinutesToMMSS(duration / 60, true)
        return durationType === 'MIN' ? minSec : `${duration}`
    }, [durationType])


    if (!open) return null
    return (
        <Modal id={modalId} open={open} pinned={pinned} disabled={disabled} className={mergeClassNames(`orbital__insights__modal`, className)}
            style={{ ...style }}
            onClick={(e) => onFocus(e)}
        >
            <ModalHeader className='insights__modal__header'>
                <HeaderActionsContainer className='insights__action__container'>
                    {tabs.map((tab) => {
                        const ignoredTabs = ['contacts', 'features']
                        if (scenarioOperationType === SCENARIO_OPERATION_TYPES.LEOP && ignoredTabs.includes(tab.value)) return null
                        else if (detachedTabs.includes(tab.value)) return null

                        return (
                            <Fragment key={tab.value}>
                                <HeaderAction
                                    key={tab.value}
                                    title={tab.label}
                                    className={`insights__action__button large 
                                        ${tab.value === selectedTab && 'active'}
                                        ${!showDetachedIcon && 'last__tab'}
                                    `}
                                    onClick={(e) => onTabSelect(e, tab.value as WindowKeys)}
                                >
                                    {tab.icon}
                                </HeaderAction>
                                {!showDetachedIcon && <HeaderActionLabel label={tab.label} />}
                            </Fragment>
                        )
                    })}
                </HeaderActionsContainer>
                <HeaderActionsContainer>
                    <div className='d-flex align-items-center gap-3'>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title={pinned ? 'Unpin' : 'Pin to Right'}
                                className={`${pinned && 'active'}`}
                                onClick={(e) => { onModeClick(e, 'pinned') }}
                            >
                                <WindowRightSvg />
                            </HeaderAction>
                            <HeaderAction title='Resize'
                                onClick={(e) => { onModeClick(e, 'resize') }}
                            >
                                <WindowResizeSvg size={windowSize} />
                            </HeaderAction>
                            {showDetachedIcon && tabs.map((tab) => {
                                if (selectedTab !== tab.value) return null
                                return (
                                    <HeaderAction title='Detach'
                                        onClick={(e) => { onDetached(e, selectedTab) }}
                                    >
                                        <WindowDetachSvg />
                                    </HeaderAction>
                                )
                            })}
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title='Close'
                                onClick={(e) => { onModeClick(e, 'close') }}
                            >
                                <CloseSvg />
                            </HeaderAction>
                        </div>
                    </div>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody className='overflow-hidden'>
                {selectedTab === 'exposure' && <SunExposer data={data} durationType={durationType} setDurationType={setDurationType} getDuration={getDuration} orbitStartOption={orbitStartOption} />}
                {selectedTab !== 'exposure' && <PassDetailsPanel data={data} componentType={selectedTab as 'contacts' | 'features'} durationType={durationType} setDurationType={setDurationType} getDuration={getDuration} orbitStartOption={orbitStartOption} />}
            </ModalBody>
        </Modal>
    )
})

OrbitalInsights.displayName = 'OrbitalInsights'

export default OrbitalInsights